import type { ReactNode } from 'react';
import { BOARD_THEMES, usePreferences, type BoardTheme, type ThemePreference } from '../app/preferences.ts';
import type { Locale } from '../i18n/messages.ts';
import { Dialog } from './Dialog.tsx';
import { Segmented } from './Segmented.tsx';
import { InfoLinks } from '../pages/InfoPage.tsx';

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { prefs, update, t } = usePreferences();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('settings.title')}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t('settings.done')}
        </button>
      }
    >
      <div className="dialog-body" style={{ paddingTop: 4 }}>
        <section className="settings-section">
          <span className="field-label">{t('settings.language')}</span>
          <Segmented<Locale>
            name="locale"
            value={prefs.locale}
            onChange={(locale) => update({ locale })}
            options={[
              { value: 'tr', label: 'Türkçe' },
              { value: 'en', label: 'English' },
            ]}
          />
        </section>

        <section className="settings-section">
          <span className="field-label">{t('settings.appearance')}</span>
          <Segmented<ThemePreference>
            name="theme"
            value={prefs.theme}
            onChange={(theme) => update({ theme })}
            options={[
              { value: 'system', label: t('settings.theme.system') },
              { value: 'light', label: t('settings.theme.light') },
              { value: 'dark', label: t('settings.theme.dark') },
            ]}
          />
        </section>

        <section className="settings-section">
          <span className="field-label">{t('settings.board')}</span>
          <div className="board-theme-grid" role="radiogroup" aria-label={t('settings.board')}>
            {(Object.keys(BOARD_THEMES) as BoardTheme[]).map((key) => (
              <label key={key} className="board-theme-option">
                <input
                  type="radio"
                  name="boardTheme"
                  checked={prefs.boardTheme === key}
                  onChange={() => update({ boardTheme: key })}
                />
                <span className="board-theme-swatch" aria-hidden="true">
                  <span style={{ background: BOARD_THEMES[key].light }} />
                  <span style={{ background: BOARD_THEMES[key].dark }} />
                  <span style={{ background: BOARD_THEMES[key].dark }} />
                  <span style={{ background: BOARD_THEMES[key].light }} />
                </span>
                <span>{t(`settings.board.${key}`)}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="settings-section">
          <span className="field-label">{t('settings.gameplay')}</span>
          <Switch label={t('settings.sound')} checked={prefs.sound} onChange={(sound) => update({ sound })} />
          <Switch
            label={t('settings.coordinates')}
            checked={prefs.coordinates}
            onChange={(coordinates) => update({ coordinates })}
          />
          <Switch
            label={t('settings.legalMoves')}
            checked={prefs.legalMoves}
            onChange={(legalMoves) => update({ legalMoves })}
          />
          <Switch label={t('settings.autoQueen')} checked={prefs.autoQueen} onChange={(autoQueen) => update({ autoQueen })} />
        </section>
      </div>
      <p className="settings-credit">{t('footer.engine')}</p>
      <div className="settings-credit" onClickCapture={onClose}>
        <InfoLinks />
      </div>
    </Dialog>
  );
}

function Switch({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="switch-row">
      <span>{label}</span>
      <span className="switch">
        <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="switch-track" />
      </span>
    </label>
  );
}
